import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";

// Global cache for buildings (shared across all instances like DirectSearchComponent)
let resourcesCache: Array<{ id: string; name: string; metadata1?: string; metadata2?: string; companyName?: string }> | null = null;
let cachePromise: Promise<Array<{ id: string; name: string; metadata1?: string; metadata2?: string; companyName?: string }>> | null = null;

interface BuildingSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  selectedBuildingIds: string[];
  onSave: (buildingIds: string[]) => void;
  onRealTimeUpdate?: (buildingIds: string[]) => Promise<void>; // For real-time saving
  title?: string;
  description?: string;
}

const BuildingSelector = ({
  isOpen,
  onClose,
  selectedBuildingIds,
  onSave,
  onRealTimeUpdate,
  title = "Selecteer gebouwen voor deze polis",
  description = "Zoek en vink meerdere gebouwen aan. We tonen de referentiecodes (bijv. 2000-EMIE-12)."
}: BuildingSelectorProps) => {
  const { session } = useSession();
  const [availableBuildings, setAvailableBuildings] = useState<Array<{ id: string; name: string; metadata1?: string; metadata2?: string; companyName?: string }>>([]);
  const [buildingsQuery, setBuildingsQuery] = useState("");
  const [tempSelection, setTempSelection] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveQueue, setSaveQueue] = useState<string[] | null>(null);
  const savingRef = useRef(false);

  // Initialize temp selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempSelection([...selectedBuildingIds]);
      setBuildingsQuery("");
      loadBuildings();
    }
  }, [isOpen, selectedBuildingIds]);

  // Load buildings using DirectSearchComponent approach with global caching
  const loadBuildings = useCallback(async () => {
    if (!session) return;
    
    try {
      setIsLoading(true);
      
      // Use cached resources if available (like DirectSearchComponent)
      if (resourcesCache) {
        setAvailableBuildings(resourcesCache);
        setIsLoading(false);
        return;
      }

      // If there's already a cache promise in progress, wait for it
      if (cachePromise) {
        const buildings = await cachePromise;
        setAvailableBuildings(buildings);
        setIsLoading(false);
        return;
      }

      // Create new cache promise (using the same approach as DirectSearchComponent)
      cachePromise = (async () => {
        // First try to get from the resources API (like DirectSearchComponent does)
        try {
          const resourcesResponse = await fetchWithAuth(`${API_URL}/resources`, { method: 'GET' }, session);
          if (resourcesResponse.ok) {
            const allResources = await resourcesResponse.json();
            const buildingResources = allResources
              .filter((r: any) => r.type === 'building')
              .map((r: any) => ({
                id: r.resourceId,
                name: r.name,
                metadata1: r.metadata1, // This often contains the building code
                metadata2: r.metadata2, // This often contains address info
                companyName: r.metadata2?.split(' - ')?.[1] || '' // Extract company if in metadata2
              }));
            
            // Cache the results
            resourcesCache = buildingResources;
            return buildingResources;
          }
        } catch (error) {
          console.log('Resources API not available, falling back to building/simple');
        }

        // Fallback to building/simple endpoint
        const res = await fetchWithAuth(`${API_URL}/building/simple`, { method: 'GET' }, session);
        if (res.ok) {
          const data = await res.json();
          const buildings = (data?.buildings || []).map((b: any) => ({ 
            id: b.id, 
            name: b.googlePlaceLongAddress || `Gebouw ${b.id}`,
            metadata1: b.id, // Building code as metadata1
            metadata2: b.googlePlaceLongAddress, // Address as metadata2
            companyName: b.company?.name
          }));
          
          // Cache the results globally
          resourcesCache = buildings;
          return buildings;
        }
        return [];
      })();
      
      const buildings = await cachePromise;
      setAvailableBuildings(buildings);
      setIsLoading(false);
      
      // Clear the promise
      cachePromise = null;
    } catch (error) {
      console.error('Error loading buildings:', error);
      setIsLoading(false);
      cachePromise = null;
    }
  }, [session]);

  // Ultra-fast local filtering with alphabetical sorting
  const filteredBuildings = useMemo(() => {
    const q = buildingsQuery.trim().toLowerCase();
    let results: Array<{ id: string; name: string; metadata1?: string; metadata2?: string; companyName?: string }> = [];
    
    if (!q) {
      // Show first 150 when no search query for instant display, sorted alphabetically
      results = availableBuildings.slice(0, 150);
    } else {
      // Fast local filtering
      for (const building of availableBuildings) {
        if (building.id.toLowerCase().includes(q) || 
            (building.name || '').toLowerCase().includes(q) ||
            (building.metadata1 || '').toLowerCase().includes(q) ||
            (building.metadata2 || '').toLowerCase().includes(q) ||
            (building.companyName || '').toLowerCase().includes(q)) {
          results.push(building);
          // Limit results for ultra-fast rendering
          if (results.length >= 100) break;
        }
      }
    }
    
    // Sort alphabetically by building.id for consistent display
    return results.sort((a, b) => a.id.localeCompare(b.id));
  }, [availableBuildings, buildingsQuery]);

  const handleSave = () => {
    onSave(tempSelection);
    onClose();
  };

  // Queued save handler - prevents duplicate API calls
  const handleRealTimeUpdate = useCallback(async (newSelection: string[]) => {
    if (!onRealTimeUpdate) return;
    
    // If already saving, queue this selection for later
    if (savingRef.current) {
      setSaveQueue(newSelection);
      return;
    }
    
    try {
      savingRef.current = true;
      setIsSaving(true);
      await onRealTimeUpdate(newSelection);
      
      // Process any queued selection after this one completes
      if (saveQueue && saveQueue !== newSelection) {
        const queuedSelection = saveQueue;
        setSaveQueue(null);
        // Small delay to prevent rapid-fire API calls
        setTimeout(() => {
          if (!savingRef.current) {
            handleRealTimeUpdate(queuedSelection);
          }
        }, 100);
      }
    } catch (error) {
      console.error('Real-time update failed:', error);
      // Revert the selection on error
      setTempSelection(selectedBuildingIds);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [onRealTimeUpdate, selectedBuildingIds, saveQueue]);

  // Clear queue when selection changes externally
  useEffect(() => {
    setSaveQueue(null);
  }, [selectedBuildingIds]);

  // Enhanced selection handler with real-time saving
  const handleBuildingToggle = useCallback(async (buildingId: string, isChecked: boolean) => {
    let newSelection: string[];
    
    if (isChecked) {
      newSelection = [...tempSelection, buildingId];
    } else {
      newSelection = tempSelection.filter(id => id !== buildingId);
    }
    
    // Update UI immediately
    setTempSelection(newSelection);
    
    // Save to backend immediately if real-time updates are enabled
    if (onRealTimeUpdate) {
      await handleRealTimeUpdate(newSelection);
    }
  }, [tempSelection, onRealTimeUpdate, handleRealTimeUpdate]);

  const handleCancel = () => {
    setTempSelection([...selectedBuildingIds]);
    onClose();
  };

  const handleSelectAll = useCallback(async () => {
    const allIds = filteredBuildings.map(b => b.id);
    const newSelection = [...tempSelection];
    allIds.forEach(id => {
      if (!newSelection.includes(id)) {
        newSelection.push(id);
      }
    });
    
    setTempSelection(newSelection);
    
    // Save to backend immediately if real-time updates are enabled
    if (onRealTimeUpdate) {
      await handleRealTimeUpdate(newSelection);
    }
  }, [filteredBuildings, tempSelection, onRealTimeUpdate, handleRealTimeUpdate]);

  const handleDeselectAll = useCallback(async () => {
    setTempSelection([]);
    
    // Save to backend immediately if real-time updates are enabled
    if (onRealTimeUpdate) {
      await handleRealTimeUpdate([]);
    }
  }, [onRealTimeUpdate, handleRealTimeUpdate]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 z-9998"
        onClick={handleCancel}
      />
      
      {/* Modal - SLICK MULTIPLE CHOICE LAYOUT */}
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-9999 max-w-6xl mx-auto rounded-2xl border border-neutral-primary/20 bg-white shadow-2xl overflow-hidden" style={{ maxHeight: '80vh' }}>
        {/* Enhanced search header */}
        <div className="border-b border-neutral-primary/10 bg-linear-to-r from-moss-primary/10 via-moss-primary/5 to-transparent p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="rounded-xl bg-moss-primary/10 p-3">
              <svg className="h-6 w-6 text-moss-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="text-xl font-semibold text-neutral-primary">{title}</h4>
                <div className="flex items-center gap-2">
                  {tempSelection.length > 0 && (
                    <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-moss-primary/20 text-moss-primary">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="text-sm font-medium">{tempSelection.length} geselecteerd</span>
                    </div>
                  )}
                  {isSaving && (
                    <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-moss-primary/10 text-moss-primary">
                      <div className="w-3 h-3 border-2 border-moss-primary/30 border-t-moss-primary rounded-full animate-spin"></div>
                      <span className="text-xs font-medium">Opslaan...</span>
                    </div>
                  )}
                </div>
              </div>
              <input
                type="text"
                className="w-full rounded-xl border border-neutral-primary/30 px-5 py-4 text-base placeholder-neutral-secondary-mid focus:outline-hidden focus:ring-2 focus:ring-moss-primary/40 focus:border-moss-primary transition-all"
                placeholder="🔍 Zoek op gebouw-ID (bijv. 2000-EMIE-12), adres, of bedrijfsnaam..."
                value={buildingsQuery}
                onChange={(e) => setBuildingsQuery(e.target.value)}
                autoFocus
              />
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-lg p-2 text-neutral-secondary-mid hover:text-neutral-primary hover:bg-neutral-100 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex items-center justify-between text-sm">
            <div className="text-neutral-secondary-mid">{description}</div>
            <div className="flex items-center gap-4 text-neutral-secondary-mid">
              <span>
                {availableBuildings.length > 0 ? `${availableBuildings.length} gebouwen beschikbaar` : 'Laden...'}
              </span>
              <span className="font-semibold text-moss-primary">
                {tempSelection.length} geselecteerd
              </span>
            </div>
          </div>
        </div>

        {/* SLICK MULTIPLE CHOICE Results area */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 200px)', minHeight: '400px' }}>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-moss-primary"></div>
            </div>
          ) : filteredBuildings.length > 0 ? (
            <div className="p-6">
              {/* Quick actions */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-neutral-primary/10">
                <div className="text-sm font-medium text-neutral-primary">
                  {filteredBuildings.length} gebouw{filteredBuildings.length !== 1 ? 'en' : ''} gevonden
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-xs px-3 py-1 rounded-lg bg-moss-primary/10 text-moss-primary hover:bg-moss-primary/20 transition-colors"
                  >
                    Alles selecteren
                  </button>
                  <button
                    type="button"
                    onClick={handleDeselectAll}
                    className="text-xs px-3 py-1 rounded-lg bg-neutral-100 text-neutral-600 hover:bg-neutral-200 transition-colors"
                  >
                    Alles deselecteren
                  </button>
                </div>
              </div>

              {/* MPC LIST VIEW */}
              <div className="space-y-1">
                {filteredBuildings.map((b) => {
                  const checked = tempSelection.includes(b.id);
                  return (
                    <label 
                      key={b.id} 
                      className="flex items-center gap-4 rounded-lg px-4 py-3 hover:bg-moss-primary/5 cursor-pointer transition-all duration-150 group border border-transparent hover:border-moss-primary/20 bg-white hover:bg-moss-primary/8"
                    >
                      <input
                        type="checkbox"
                        className="h-5 w-5 rounded-sm border-neutral-primary/30 text-moss-primary focus:ring-moss-primary/30 transition-colors shrink-0"
                        checked={checked}
                        onChange={(e) => handleBuildingToggle(b.id, e.target.checked)}
                      />
                      
                      {/* Building ID - Most prominent */}
                      <div className="font-bold text-neutral-primary group-hover:text-moss-primary transition-colors text-base shrink-0 min-w-[120px]">
                        {b.id}
                      </div>

                      {/* Address/Name */}
                      <div className="flex-1 min-w-0 text-sm text-neutral-secondary-mid">
                        {(b as any).googlePlaceLongAddress || b.metadata2 || 'Geen adres'}
                      </div>

                      {/* Company Name */}
                      {b.companyName && (
                        <div className="text-sm text-neutral-secondary-mid font-medium shrink-0 max-w-[200px] truncate">
                          🏢 {b.companyName}
                        </div>
                      )}

                      {/* Selection indicator */}
                      {checked && (
                        <div className="rounded-full bg-moss-primary p-1 shrink-0">
                          <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="mx-auto w-24 h-24 rounded-full bg-neutral-50 flex items-center justify-center mb-4">
                <svg className="h-12 w-12 text-neutral-secondary-mid/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="text-lg font-medium text-neutral-primary mb-2">
                {buildingsQuery ? 'Geen gebouwen gevonden' : 'Gebouwen worden geladen...'}
              </div>
              <div className="text-sm text-neutral-secondary-mid">
                {buildingsQuery ? 'Probeer een andere zoekterm' : 'Een moment geduld...'}
              </div>
            </div>
          )}
        </div>

        {/* Enhanced footer with selection summary */}
        <div className="flex items-center justify-between border-t border-neutral-primary/10 bg-linear-to-r from-moss-primary/5 to-neutral-50/80 px-6 py-5">
          <div className="flex items-center gap-4">
            {filteredBuildings.length > 0 && (
              <div className="flex items-center gap-3 text-neutral-secondary-mid">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 rounded-full bg-moss-primary"></span>
                  <span className="font-medium">{filteredBuildings.length} van {availableBuildings.length} gebouwen</span>
                </div>
              </div>
            )}
            {tempSelection.length > 0 && (
              <div className="flex items-center gap-2 text-moss-primary">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="font-semibold">{tempSelection.length} geselecteerd</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button 
              type="button" 
              className="rounded-xl bg-neutral-200 px-4 py-3 text-sm font-medium text-neutral-primary hover:bg-neutral-300 transition-colors"
              onClick={handleCancel}
            >
              Sluiten
            </button>
            {!onRealTimeUpdate && (
              <button 
                type="button" 
                className="rounded-xl bg-moss-primary px-6 py-3 text-base font-semibold text-white hover:bg-moss-primary/90 transition-colors shadow-lg flex items-center gap-2"
                onClick={handleSave}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Opslaan ({tempSelection.length})
              </button>
            )}
            {onRealTimeUpdate && (
              <div className="text-sm text-moss-primary font-medium flex items-center gap-2">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Automatisch opgeslagen
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BuildingSelector;
